import moment from "moment-timezone";

// ═══════════════════════════════════════════════════════════════════════════════
// DAY DIFFERENCE HELPER
// Calculates duration of a booking in days/hours
// 
// CRITICAL: Uses moment-timezone for DST-safe calculations.
// A 2-hour rental should always be 2 hours, even if DST changes during the rental.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Calculate the number of days for a reservation (legacy function)
 * 
 * @param {Object} params
 * @param {number} params.startTime - Start hour (decimal, e.g., 9.5 = 9:30 AM)
 * @param {number} params.endTime - End hour (decimal)
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string} params.timezone - IANA timezone (optional, defaults to UTC)
 * @returns {number} - Number of days
 */
const dayDifferenceHelper = ({ startTime, endTime, startDate, endDate, timezone }) => {
    const tz = timezone || 'UTC';
    
    // Parse dates in the specified timezone for DST-safe calculation
    const momentStartDate = moment.tz(startDate, 'YYYY-MM-DD', tz);
    const momentEndDate = moment.tz(endDate, 'YYYY-MM-DD', tz);
    
    let dayDifference = momentEndDate.diff(momentStartDate, 'days');

    if (dayDifference == 1) {
        dayDifference = (Number((24 - startTime)) + Number(endTime)) <= 24 ? 1 : 2;
    } else { 
        dayDifference += 1; 
    }

    return dayDifference;
};

export default dayDifferenceHelper;

/**
 * Calculate exact hours between two times on potentially different dates
 * DST-safe: uses actual timezone-aware moments
 * 
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {number} startTime - Start hour (decimal)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {number} endTime - End hour (decimal)
 * @param {string} timezone - IANA timezone
 * @returns {number} - Exact hours (fractional)
 */
export function calculateExactHours(startDate, startTime, endDate, endTime, timezone) {
    const tz = timezone || 'UTC';
    
    // Create precise moment objects
    const startHour = Math.floor(startTime);
    const startMinute = Math.round((startTime - startHour) * 60);
    const endHour = Math.floor(endTime);
    const endMinute = Math.round((endTime - endHour) * 60);
    
    const start = moment.tz(startDate, 'YYYY-MM-DD', tz)
        .hour(startHour)
        .minute(startMinute)
        .second(0);
    
    const end = moment.tz(endDate, 'YYYY-MM-DD', tz)
        .hour(endHour)
        .minute(endMinute)
        .second(0);
    
    // moment.diff handles DST automatically
    return end.diff(start, 'hours', true);
}

/**
 * Format date to UTC ISO string
 */
export async function utcDateFormat(date) {
    return moment.utc(date).format('YYYY-MM-DD');
}

/**
 * Format date to YYYY-MM-DD
 */
export async function dateFormat(date) {
    return moment(date).format('YYYY-MM-DD');
}
